import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { http, formatErr } from "@/lib/api";
import { ArrowLeft, FloppyDisk, Wrench, Buildings } from "@phosphor-icons/react";

export default function NewJob() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const prefStoreId = sp.get("store_id") || "";
  const prefEqId = sp.get("equipment_id") || "";

  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: items = [] } = useQuery({ queryKey: ["repair-items"], queryFn: () => http.get("/repair-items").then(r => r.data) });
  const { data: equipment = [] } = useQuery({ queryKey: ["equipment"], queryFn: () => http.get("/equipment").then(r => r.data) });

  const [storeId, setStoreId] = useState(prefStoreId);
  // Job type — required choice once store is picked
  const [jobType, setJobType] = useState(prefEqId ? "equipment_repair" : "");
  const [repairItemId, setRepairItemId] = useState("");
  const [equipmentId, setEquipmentId] = useState(prefEqId);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [slaHours, setSlaHours] = useState(48);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (prefStoreId) setStoreId(prefStoreId);
  }, [prefStoreId]);

  useEffect(() => {
    if (prefEqId && equipment.length) {
      const eq = equipment.find(e => e.id === prefEqId);
      if (eq) {
        setStoreId(eq.store_id);
        setJobType("equipment_repair");
        setTitle(prev => prev || `${eq.name} — `);
      }
    }
  }, [prefEqId, equipment]);

  const onChooseType = (t) => {
    setJobType(t);
    // Clear cross-type selections so it's truly one-or-the-other
    if (t === "building_repair") setEquipmentId("");
    if (t === "equipment_repair") setRepairItemId("");
    setErr("");
  };

  const onPickItem = (id) => {
    setRepairItemId(id);
    const it = items.find(x => x.id === id);
    if (it) {
      setTitle(it.name);
      setDescription(it.description || "");
      setPriority(it.default_priority || "normal");
      setSlaHours(it.default_sla_hours || 48);
      setEstimatedCost(it.default_estimated_cost || 0);
    }
  };

  const onPickEquipment = (id) => {
    setEquipmentId(id);
    const eq = equipment.find(e => e.id === id);
    if (eq) {
      setTitle(prev => prev || `${eq.name} — `);
    }
  };

  const eqForStore = equipment.filter(e => e.store_id === storeId);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (!storeId) throw new Error("Store is required");
      if (!jobType) throw new Error("Job type is required");
      if (jobType === "equipment_repair" && !equipmentId) throw new Error("Equipment is required for an Equipment Repair");
      const payload = {
        store_id: storeId,
        job_type: jobType,
        title,
        description,
        priority,
        sla_hours: Number(slaHours),
        estimated_cost: Number(estimatedCost),
      };
      if (jobType === "equipment_repair") {
        payload.equipment_id = equipmentId;
      } else {
        payload.repair_item_id = repairItemId || null;
      }
      const r = await http.post("/jobs", payload);
      navigate(`/jobs/${r.data.id}`);
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} data-testid="newjob-back" className="text-xs text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
      <div className="overline">New</div>
      <h1 className="text-3xl font-heading font-semibold tracking-tight mb-6">Create job</h1>

      <form onSubmit={submit} className="rmms-card p-6 space-y-5" data-testid="newjob-form">
        {/* Step 1 — Store (always required, blocks everything else) */}
        <div>
          <label className="overline mb-1.5 block">
            <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-slate-900 text-white text-[10px] font-mono-data mr-1.5">1</span>
            Store *
          </label>
          <select required value={storeId} onChange={e => { setStoreId(e.target.value); setEquipmentId(""); setJobType(""); }} data-testid="newjob-store-select" className="rmms-input">
            <option value="">Select store…</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Step 2 — Job type (mutually exclusive) — locked until store picked */}
        <div>
          <label className="overline mb-1.5 block">
            <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-white text-[10px] font-mono-data mr-1.5 ${storeId ? "bg-slate-900" : "bg-slate-300"}`}>2</span>
            Job type *
          </label>
          {!storeId ? (
            <div className="text-xs text-slate-400 italic px-1" data-testid="newjob-type-locked">Pick a store first to choose the job type.</div>
          ) : (
            <div className="grid grid-cols-2 gap-2" data-testid="newjob-type-picker">
              <button
                type="button"
                onClick={() => onChooseType("equipment_repair")}
                data-testid="newjob-type-equipment"
                className={`p-3 border rounded-md text-left transition-colors ${
                  jobType === "equipment_repair" ? "border-[#002FA7] bg-blue-50 ring-1 ring-[#002FA7]" : "border-slate-200 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Wrench size={16} className={jobType === "equipment_repair" ? "text-[#002FA7]" : "text-slate-500"} />
                  <span className="text-sm font-medium">Equipment Repair</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Specific equipment from the store inventory</div>
              </button>
              <button
                type="button"
                onClick={() => onChooseType("building_repair")}
                data-testid="newjob-type-building"
                className={`p-3 border rounded-md text-left transition-colors ${
                  jobType === "building_repair" ? "border-[#002FA7] bg-blue-50 ring-1 ring-[#002FA7]" : "border-slate-200 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Buildings size={16} className={jobType === "building_repair" ? "text-[#002FA7]" : "text-slate-500"} />
                  <span className="text-sm font-medium">Building Repair</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Structural / general (e.g. ceiling, plumbing, doors)</div>
              </button>
            </div>
          )}
        </div>

        {/* Step 3a — Equipment Repair: pick equipment */}
        {storeId && jobType === "equipment_repair" && (
          <div>
            <label className="overline mb-1.5 block">
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-slate-900 text-white text-[10px] font-mono-data mr-1.5">3</span>
              Equipment *
            </label>
            <select required value={equipmentId} onChange={e => onPickEquipment(e.target.value)} data-testid="newjob-equipment-select" className="rmms-input">
              <option value="">Select equipment…</option>
              {eqForStore.map(e => <option key={e.id} value={e.id}>{e.name} {e.model ? `· ${e.model}` : ""}</option>)}
            </select>
            {eqForStore.length === 0 && (
              <div className="text-[11px] text-slate-500 mt-1" data-testid="newjob-no-equipment">This store has no equipment registered yet.</div>
            )}
          </div>
        )}

        {/* Step 3b — Building Repair: optional template */}
        {storeId && jobType === "building_repair" && (
          <div>
            <label className="overline mb-1.5 block">
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-slate-900 text-white text-[10px] font-mono-data mr-1.5">3</span>
              Building repair template <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
            </label>
            <select value={repairItemId} onChange={e => onPickItem(e.target.value)} data-testid="newjob-item-select" className="rmms-input">
              <option value="">Custom (no template)</option>
              {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>
        )}

        {/* Remaining fields — only visible once type is chosen */}
        {storeId && jobType && (
          <>
            <div>
              <label className="overline mb-1.5 block">Title *</label>
              <input required value={title} onChange={e => setTitle(e.target.value)} data-testid="newjob-title-input" className="rmms-input" />
            </div>

            <div>
              <label className="overline mb-1.5 block">Description</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} data-testid="newjob-description-input"
                rows={3} className="w-full rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-[#002FA7]" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="overline mb-1.5 block">Priority</label>
                <select value={priority} onChange={e => setPriority(e.target.value)} data-testid="newjob-priority-select" className="rmms-input">
                  {["low", "normal", "high", "urgent"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-1.5 block">SLA hours</label>
                <input type="number" min={1} value={slaHours} onChange={e => setSlaHours(e.target.value)} data-testid="newjob-sla-input" className="rmms-input font-mono-data" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Estimated cost (£)</label>
                <input type="number" step="0.01" min={0} value={estimatedCost} onChange={e => setEstimatedCost(e.target.value)} data-testid="newjob-cost-input" className="rmms-input font-mono-data" />
              </div>
            </div>
          </>
        )}

        {err && <div data-testid="newjob-error" className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
          <button type="button" onClick={() => navigate(-1)} className="h-10 px-4 text-sm border border-slate-200 rounded-md hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={busy || !storeId || !jobType} data-testid="newjob-submit-button"
            className="h-10 px-5 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" style={{ background: "#002FA7" }}>
            <FloppyDisk size={14} weight="bold" /> {busy ? "Creating…" : "Create job"}
          </button>
        </div>
      </form>
    </div>
  );
}
